/**
 * Actions Card Component
 *
 * Displays action buttons to call Plaid API endpoints.
 * Buttons are only enabled when a Plaid connection is active.
 *
 * @fileoverview Actions card with Plaid API action buttons
 *
 * @example
 * <ActionsCard
 *   onActionResult={(result) => setActionResult(result)}
 *   onActionLoading={(loading) => setIsLoading(loading)}
 * />
 */

import React, { useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  Button,
  Icon,
  Text,
  HStack,
  Badge,
  Box,
  Divider,
} from '@chakra-ui/react';
import {
  MdReceipt,
  MdPlayArrow,
} from 'react-icons/md';
import { useUser } from '../../Context/userContext';
import { supabase } from '../../lib/supabase';
import { apiFetch } from '../../lib/api';
import { ActionResult } from './ActionResultDisplay';
import { AccountSelector } from '../common/AccountSelector';
import { toUserMessage } from '../../lib/apiError';

// Re-export ActionResult for backward compatibility
export type { ActionResult };

/**
 * Props for ActionsCard component
 */
interface ActionsCardProps {
  /** Callback when an action result is received */
  onActionResult?: (result: ActionResult | null) => void;
  /** Callback when action loading state changes */
  onActionLoading?: (isLoading: boolean) => void;
}

/**
 * Action button configuration
 */
interface ActionConfig {
  /** Display label for the button */
  label: string;
  /** Icon component to display */
  icon: React.ElementType;
  /** API endpoint to call */
  endpoint: string;
  /** Description of what the action does */
  description: string;
}

/**
 * Available Plaid API actions
 */
const ACTIONS: ActionConfig[] = [
  {
    label: 'Transactions',
    icon: MdReceipt,
    endpoint: '/api/transactions',
    description: 'Transaction history',
  },
];

/**
 * Actions Card Component
 *
 * Displays a card with action buttons for calling Plaid API endpoints.
 * Each button triggers an API call and passes the result to the parent.
 *
 * @param props - Component props
 * @returns Actions card component
 */
export const ActionsCard: React.FC<ActionsCardProps> = ({
  onActionResult,
  onActionLoading,
 }) => {
  const { plaidTokenStatus, selectedTenant, selectedAccountId, setSelectedAccountId } = useUser();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Check if actions should be enabled
  const isConnectionActive = plaidTokenStatus?.status === 'ACTIVE';

  /**
   * Handle action button click
   *
   * Makes an API call to the specified endpoint and passes
   * the result to the parent component via callback.
   *
   * @param action - Action configuration
   */
  const handleAction = useCallback(
    async (action: ActionConfig) => {
      if (!selectedTenant || !isConnectionActive) return;

      setLoadingAction(action.label);
      onActionLoading?.(true);

      try {
        // Get auth token for the request
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Not authenticated');
        }

        // Build endpoint URL with optional account filter
        const url = selectedAccountId
          ? `${action.endpoint}?accountId=${selectedAccountId}`
          : action.endpoint;

        // Call the API endpoint using apiFetch (handles VITE_API_HOST)
        const response = await apiFetch(url, {
          method: 'GET',
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        }, session.access_token);

        const data = await response.json();

        // Create result object
        const result: ActionResult = {
          action: action.label,
          success: response.ok,
          data: response.ok ? data : undefined,
          error: !response.ok ? data.message || `HTTP ${response.status}` : undefined,
          timestamp: new Date().toISOString(),
        };

        onActionResult?.(result);
      } catch (error) {
        // Handle network or other errors
        const errorMessage = toUserMessage(error, 'Please try again in a moment.');

        onActionResult?.({
          action: action.label,
          success: false,
          error: errorMessage,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setLoadingAction(null);
        onActionLoading?.(false);
      }
    },
    [selectedTenant, isConnectionActive, selectedAccountId, onActionResult, onActionLoading]
  );

  return (
    <Card bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px">
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={MdPlayArrow} color="accent.primary" boxSize={5} />
            <Heading size="sm" color="text.primary">
              Actions
            </Heading>
          </HStack>
          {isConnectionActive && (
            <Badge colorScheme="green" variant="subtle" fontSize="2xs">
              Ready
            </Badge>
          )}
        </HStack>
      </CardHeader>

      <CardBody pt={2}>
        <VStack spacing={3} align="stretch">
          {/* Info text when no connection */}
          {!isConnectionActive && (
            <Text color="text.muted" fontSize="sm" textAlign="center" py={2}>
              Connect a bank account to enable actions
            </Text>
          )}

          {/* Account Selector */}
          {isConnectionActive && (
            <Box>
              <AccountSelector
                value={selectedAccountId}
                onChange={setSelectedAccountId}
                showAllOption
                allOptionLabel="All Accounts"
                label="Filter by Account"
                size="sm"
              />
              <Divider my={3} borderColor="xacto.mutedNavy" />
            </Box>
          )}

          {/* Action buttons */}
          {ACTIONS.map((action) => (
            <Button
              key={action.label}
              leftIcon={<Icon as={action.icon} />}
              variant="outline"
              colorScheme="cyan"
              borderColor="xacto.electricCyan"
              color="accent.primary"
              w="full"
              justifyContent="flex-start"
              isLoading={loadingAction === action.label}
              loadingText={`Fetching ${action.label}...`}
              isDisabled={!isConnectionActive || loadingAction !== null}
              onClick={() => handleAction(action)}
              _hover={{
                bg: 'bg.muted',
                borderColor: 'cyan.400',
              }}
              _disabled={{
                opacity: 0.5,
                cursor: 'not-allowed',
                _hover: {
                  bg: 'transparent',
                  borderColor: 'accent.primary',
                },
              }}
            >
              <VStack align="start" spacing={0} flex={1}>
                <Text fontWeight="medium">{action.label}</Text>
                <Text fontSize="xs" color="text.muted" fontWeight="normal">
                  {action.description}
                </Text>
              </VStack>
            </Button>
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default ActionsCard;
